import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { KSK1090_R01Dto } from './Dto_KSK1090_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface KSK1090_R01Filter {
    compCd: string;
    customCd: string;
    customNm: string;
    transportDt: DateRange | undefined;
    workplaceCd: string;
    logicCenter: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
    inoutClass: string;
    itemGroup: string;
}

// (소계 제거로 확장 타입 불필요)

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_KSK1090_R01 {
    KSK1090_R01Filter: KSK1090_R01Filter;
    KSK1090_R01PastFilter: KSK1090_R01Filter;
    setKSK1090_R01Filter: (filter: Partial<KSK1090_R01Filter>) => void;
    setKSK1090_R01PastFilter: (filter: Partial<KSK1090_R01Filter>) => void;

    // === 리스트 관련 ===
    KSK1090_R01List: KSK1090_R01Dto[];
    setKSK1090_R01List: (KSK1090_R01List: KSK1090_R01Dto[]) => void;
    insertKSK1090_R01List: (data: KSK1090_R01Dto[], index?: number) => void;
    removeKSK1090_R01List: (index: number) => void;
    updateKSK1090_R01List: (data: KSK1090_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedKSK1090_R01List: string[];
    setSelectedKSK1090_R01List: (idList: string[]) => void;
    addSelectedKSK1090_R01List: (idList: string[]) => void;
    removeSelectedKSK1090_R01List: (idList?: string[]) => void;
    initSelectedKSK1090_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // (소계 제거) 추가 상태 없음

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultKSK1090_R01Filter: KSK1090_R01Filter = {
    compCd: '',
    inoutClass: '',
    customCd: '',
    customNm: '',
    transportDt: defaultDateRange,
    workplaceCd: '',
    logicCenter: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
    itemGroup: '',
};

/**
 * Zustand 스토어 정의
 */
export const useKSK1090_R01Store = create<Store_KSK1090_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_KSK1090_R01, 'KSK1090_R01Filter' | 'KSK1090_R01PastFilter'>) => 
        (filter: Partial<KSK1090_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /** 
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        KSK1090_R01Filter: defaultKSK1090_R01Filter,
        KSK1090_R01PastFilter: defaultKSK1090_R01Filter,
        setKSK1090_R01Filter: createFilterSetter('KSK1090_R01Filter'),
        setKSK1090_R01PastFilter: createFilterSetter('KSK1090_R01PastFilter'),

        // === 리스트 관련 ===
        KSK1090_R01List: [],
        setKSK1090_R01List: (KSK1090_R01List: KSK1090_R01Dto[]) => set({ KSK1090_R01List }),
        insertKSK1090_R01List: (data: KSK1090_R01Dto[], index?: number) => set((state) => {
            const _KSK1090_R01List = [...state.KSK1090_R01List];
            
            if (index !== undefined) {
                _KSK1090_R01List.splice(index, 0, ...data);
            } else {
                _KSK1090_R01List.push(...data);
            }
            return { ...state, KSK1090_R01List: _KSK1090_R01List };
        }),
        removeKSK1090_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            KSK1090_R01List: state.KSK1090_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateKSK1090_R01List: (data: KSK1090_R01Dto, index: number) => set((state) => {
            const _KSK1090_R01List = [...state.KSK1090_R01List];
            _KSK1090_R01List[index] = data;
            return { ...state, KSK1090_R01List: _KSK1090_R01List };
        })
        ,

        // === 선택 관련 ===
        selectedKSK1090_R01List: [],
        setSelectedKSK1090_R01List: (idList: string[]) => set({ selectedKSK1090_R01List: idList }),
        addSelectedKSK1090_R01List: (idList: string[]) => set((state) => {
            const _selectedKSK1090_R01List = [...state.selectedKSK1090_R01List];
            _selectedKSK1090_R01List.push(...idList.filter(value => !_selectedKSK1090_R01List.includes(value)));
            return { ...state, selectedKSK1090_R01List: _selectedKSK1090_R01List };
        }),
        removeSelectedKSK1090_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedKSK1090_R01List: [] };
            }
            
            return {
                ...state,
                selectedKSK1090_R01List: removeItemsFromArray(state.selectedKSK1090_R01List, idList),
            };
        }),
        initSelectedKSK1090_R01List: () => set({ selectedKSK1090_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        // === 통합 소계 관련 ===
        

        // === 전체 초기화 ===
        initData: () => set(() => ({
            KSK1090_R01Filter: defaultKSK1090_R01Filter,
            KSK1090_R01PastFilter: defaultKSK1090_R01Filter,
            KSK1090_R01List: [],
            selectedKSK1090_R01List: [],
            columnState: null,
            allCount: 0,
		
            
        })),
    };
});


